import { Orcamento, PerfilMei } from '@/contracts/schemas';

const DB_NAME = 'zaporca_idb_v1';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase | null> | null = null;

function isIndexedDbAvailable(): boolean {
  try {
    return typeof window !== 'undefined' && typeof window.indexedDB !== 'undefined';
  } catch {
    return false;
  }
}

export function openDatabase(): Promise<IDBDatabase | null> {
  if (!isIndexedDbAvailable()) return Promise.resolve(null);
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve) => {
    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('orcamentos')) {
          const store = db.createObjectStore('orcamentos', { keyPath: 'id' });
          store.createIndex('uuid', 'uuid', { unique: false });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('dataCriacao', 'dataCriacao', { unique: false });
        }
        if (!db.objectStoreNames.contains('perfil')) {
          db.createObjectStore('perfil', { keyPath: 'id' });
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        console.warn('ZapOrca: Falha ao abrir IndexedDB. Usando fallback de localStorage.');
        resolve(null);
      };
    } catch (e) {
      console.warn('ZapOrca: Exceção ao acessar IndexedDB:', e);
      resolve(null);
    }
  });

  return dbPromise;
}

export async function idbGetAllOrcamentos(): Promise<Orcamento[]> {
  const db = await openDatabase();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction('orcamentos', 'readonly');
      const store = tx.objectStore('orcamentos');
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => resolve([]);
    } catch {
      resolve([]);
    }
  });
}

export async function idbSaveOrcamento(orcamento: Orcamento): Promise<void> {
  const db = await openDatabase();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction('orcamentos', 'readwrite');
      const store = tx.objectStore('orcamentos');
      store.put(orcamento);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export async function idbDeleteOrcamento(id: string): Promise<void> {
  const db = await openDatabase();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction('orcamentos', 'readwrite');
      const store = tx.objectStore('orcamentos');
      store.delete(id);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export async function idbGetPerfil(): Promise<PerfilMei | null> {
  const db = await openDatabase();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction('perfil', 'readonly');
      const store = tx.objectStore('perfil');
      const req = store.getAll();
      req.onsuccess = () => {
        const list = req.result;
        resolve(list && list.length > 0 ? list[0] : null);
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function idbSavePerfil(perfil: PerfilMei): Promise<void> {
  const db = await openDatabase();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction('perfil', 'readwrite');
      const store = tx.objectStore('perfil');
      store.put(perfil);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export async function idbClearAll(): Promise<void> {
  const db = await openDatabase();
  if (!db) return;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(['orcamentos', 'perfil'], 'readwrite');
      tx.objectStore('orcamentos').clear();
      tx.objectStore('perfil').clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}
